import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "motion/react";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { ClipPhoto, EASE_OUT, MaskWords, PageFrame, Photo, Seo, useMediaQuery } from "../../kit.jsx";
import { fieldNotes, people } from "../../data/content.js";
import { AAROH_IMAGES } from "../../data/images.js";
import { AAROH_DATES, AAROH_REEL, MEDIA_COUNTS, MEETINGS, STORY_LIST, albumPhoto } from "./data.js";
import { InlineClip, RollLabel } from "./shared.jsx";
import "./stories.css";

const [COVER, ...OTHER_STORIES] = STORY_LIST;

const MORE_ROWS = [
  ...OTHER_STORIES.map((story) => ({
    to: `/stories/${story.slug}`,
    kind: story.kind,
    when: story.date,
    title: story.title,
    summary: story.summary,
    length: `${story.minutes} min read`,
    image: story.lead.src,
  })),
  {
    to: "/programmes/aaroh",
    kind: "Programme account",
    when: "August 2026",
    title: "AAROH: event recap",
    summary: "Two days, 363 young innovators and thirteen recognised teams.",
    length: "Recap",
    image: AAROH_IMAGES.room,
  },
  {
    to: "/programmes/aurora",
    kind: "Programme account",
    when: "Online",
    title: "Aurora: programme results",
    summary: "The winners, 1,500 participants from 27+ countries and the Top 20 teams that entered incubation.",
    length: "Results",
    image: "/images/grid-photo-10-p8.jpg",
  },
  {
    to: "/programmes/metamorphosis",
    kind: "Programme account",
    when: "IIT Delhi",
    title: "Metamorphosis: outreach partnership",
    summary: "Led by Tale of Humankind Foundation and IIT Delhi. GRID was the outreach partner.",
    length: "Our role",
    image: "/images/grid-photo-01-p1.jpg",
  },
  {
    to: "/meetings",
    kind: "Archive",
    when: "July 2026",
    title: "People we've met",
    summary: "Visits and conversations. A meeting is not an endorsement.",
    length: `${MEETINGS.length} records`,
    image: MEETINGS[0].image,
  },
  {
    to: "/media",
    kind: "Photos",
    when: "All programmes",
    title: "Photographs from GRID programmes",
    summary: "Documentary images, each with its programme context.",
    length: `${MEDIA_COUNTS.photos} photos`,
    image: AAROH_IMAGES.roomWide,
  },
];

const WINNERS = people.filter((person) => ["jerovin-floyd-vincent-joseph", "gantavya-rohilla"].includes(person.slug));

function Masthead() {
  const reduce = useReducedMotion();
  return (
    <header className="st-hub__mast">
      <div className="st-hub__plate">
        <motion.p
          className="st-mono"
          initial={reduce ? false : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE_OUT }}
        >
          Stories from GRID
        </motion.p>
        <MaskWords as="h1" text="Notes from the rooms we've been in." onMount delay={0.05} />
      </div>
      <motion.div
        className="st-hub__lines"
        initial={reduce ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.4, ease: EASE_OUT }}
      >
        <p>Read about why GRID started and what students learn by working on a project. For event facts and results, start with the programme pages.</p>
        <ul aria-label="In this section">
          <li><a href="#st-more"><strong>{STORY_LIST.length}</strong> field notes</a></li>
          <li><Link to="/media"><strong>{MEDIA_COUNTS.photos}</strong> photographs and {MEDIA_COUNTS.clips} clips</Link></li>
          <li><Link to="/meetings"><strong>{MEETINGS.length}</strong> meeting records</Link></li>
        </ul>
      </motion.div>
    </header>
  );
}

function CoverStory() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, (v) => `${(Math.min(1, Math.max(0, v)) - 0.5) * 12}%`);
  return (
    <section className="st-cover" ref={ref} aria-labelledby="st-cover-title">
      <Link className="st-cover__link" to={`/stories/${COVER.slug}`}>
        <motion.div
          className="st-cover__frame"
          initial={reduce ? false : { clipPath: "inset(10% 6% 10% 6% round 28px)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
          transition={{ duration: 1.3, delay: 0.15, ease: EASE_OUT }}
        >
          <motion.div className="st-cover__img" style={reduce ? undefined : { y }}>
            <Photo src={COVER.lead.src} alt={COVER.lead.alt} sizes="(max-width: 900px) 100vw, 90vw" priority />
          </motion.div>
        </motion.div>
        <div className="st-cover__card">
          <p className="st-mono">Cover story · {COVER.date} · {COVER.minutes} min read</p>
          <h2 id="st-cover-title">{COVER.title}</h2>
          <p>{COVER.summary}</p>
          <span className="gh-btn gh-btn--fill">
            <RollLabel>Read the story</RollLabel>
            <ArrowRight weight="bold" aria-hidden="true" />
          </span>
        </div>
      </Link>
      <p className="st-cover__caption">{COVER.lead.caption}</p>
    </section>
  );
}

function MoreToRead() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const canHover = useMediaQuery("(hover: hover) and (pointer: fine) and (min-width: 900px)");
  const preview = canHover && !reduce;
  const [active, setActive] = useState(null);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const y = useSpring(py, { stiffness: 320, damping: 42, mass: 0.6 });

  function onMove(event) {
    if (!preview || !ref.current) return;
    // The preview rides down the right edge of the list, level with the pointer,
    // so it never covers the titles.
    const box = ref.current.getBoundingClientRect();
    px.set(box.width - 250);
    py.set(Math.min(box.height - 180, Math.max(0, event.clientY - box.top - 90)));
  }

  return (
    <section className="st-more" id="st-more" aria-labelledby="st-more-title">
      <header className="st-more__head">
        <MaskWords id="st-more-title" text="More to read." />
        <p>Field notes, programme accounts and the two archives.</p>
      </header>
      <div className="st-more__list" ref={ref} onPointerMove={onMove} onPointerLeave={() => setActive(null)}>
        <div className="st-more__labels" aria-hidden="true">
          <span>Kind</span><span>Title</span><span>Length</span>
        </div>
        <ol>
          {MORE_ROWS.map((row, index) => (
            <motion.li
              key={row.to}
              initial={reduce ? false : { opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7, delay: Math.min(index, 4) * 0.05, ease: EASE_OUT }}
            >
              <Link to={row.to} onPointerEnter={() => setActive(index)} onFocus={() => setActive(null)}>
                <span className="st-more__meta"><strong>{row.kind}</strong><span>{row.when}</span></span>
                <span className="st-more__title">
                  <strong>{row.title}</strong>
                  <span>{row.summary}</span>
                </span>
                <span className="st-more__len">{row.length}</span>
                <span className="st-more__thumb" aria-hidden="true"><Photo src={row.image} alt="" sizes="120px" /></span>
                <ArrowUpRight className="st-more__arrow" weight="bold" aria-hidden="true" />
              </Link>
            </motion.li>
          ))}
        </ol>
        {preview && (
          <motion.div
            className="st-more__preview"
            aria-hidden="true"
            style={{ x: px, y }}
            initial={false}
            animate={active === null ? { opacity: 0, scale: 0.92, clipPath: "inset(12% 12% 12% 12% round 20px)" } : { opacity: 1, scale: 1, clipPath: "inset(0% 0% 0% 0% round 20px)" }}
            transition={{ duration: 0.45, ease: EASE_OUT }}
          >
            {MORE_ROWS.map((row, index) => (
              <img
                key={row.to}
                src={row.image}
                alt=""
                loading="lazy"
                className={index === active ? "is-on" : undefined}
              />
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}

const AAROH_PICKS = ["M102", "M609"].map(albumPhoto);

function AarohInPictures() {
  return (
    <section className="st-aaroh" aria-labelledby="st-aaroh-title">
      <header className="st-aaroh__head">
        <p className="st-mono">AAROH · IIIT Delhi · {AAROH_DATES}</p>
        <MaskWords id="st-aaroh-title" text="AAROH, in pictures." />
      </header>
      <div className="st-aaroh__body">
        <InlineClip
          className="st-aaroh__reel"
          clip={{ ...AAROH_REEL, alt: "A short silent reel of AAROH: the main hall, teams talking at their laptops, a speaker at the lectern and phone torches in the darkened hall.", caption: "Two days at AAROH in one short reel." }}
        />
        <div className="st-aaroh__side">
          <p>363 young innovators spent two days building, presenting and meeting mentors. The album has {MEDIA_COUNTS.aaroh} photographs and clips from the rooms.</p>
          {AAROH_PICKS.map((item) => (
            <ClipPhoto key={item.id} className="st-aaroh__photo" src={item.srcSmall} alt={item.alt} caption={item.caption} sizes="(max-width: 900px) 100vw, 30vw" />
          ))}
          <div className="gh-actions">
            <Link className="gh-btn gh-btn--fill" to="/media?filter=AAROH">
              <RollLabel>Open the AAROH album</RollLabel>
              <ArrowRight weight="bold" aria-hidden="true" />
            </Link>
            <Link className="gh-link" to="/programmes/aaroh">Read the recap <ArrowRight weight="bold" aria-hidden="true" /></Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function FieldNotesRail() {
  const railRef = useRef(null);
  const reduce = useReducedMotion();
  function nudge(direction) {
    const rail = railRef.current;
    if (!rail) return;
    const step = rail.querySelector("li")?.getBoundingClientRect().width ?? 400;
    rail.scrollBy({ left: direction * (step + 20), behavior: reduce ? "auto" : "smooth" });
  }
  return (
    <section className="st-notes" data-nav-tone="dark" aria-labelledby="st-notes-title">
      <div className="st-notes__inner">
        <header className="st-notes__head">
          <div>
            <p className="st-mono">Field notes</p>
            <MaskWords id="st-notes-title" text="Five photographs, five short notes." />
          </div>
          <div className="st-notes__nav">
            <button type="button" onClick={() => nudge(-1)} aria-label="Previous field note"><ArrowLeft weight="bold" aria-hidden="true" /></button>
            <button type="button" onClick={() => nudge(1)} aria-label="Next field note"><ArrowRight weight="bold" aria-hidden="true" /></button>
          </div>
        </header>
        <ul className="st-notes__rail" ref={railRef} tabIndex={0} aria-label="Field notes, scroll sideways">
          {fieldNotes.map((note, index) => (
            <motion.li
              key={note.src}
              initial={reduce ? false : { opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.9, delay: index * 0.06, ease: EASE_OUT }}
            >
              <figure>
                <div className="st-notes__img"><Photo src={note.src} alt={note.alt} sizes="(max-width: 900px) 80vw, 30vw" /></div>
                <figcaption>{note.caption}</figcaption>
              </figure>
            </motion.li>
          ))}
        </ul>
        <Link className="gh-btn gh-btn--ghost-dark" to="/media"><RollLabel>See every photograph</RollLabel></Link>
      </div>
    </section>
  );
}

function AuroraBuilders() {
  return (
    <section className="st-winners" aria-labelledby="st-winners-title">
      <header className="st-winners__head">
        <MaskWords id="st-winners-title" text="Two Aurora builders." />
        <p>Aurora is a Project GRID programme. Meet its overall winner and its Best Innovation winner.</p>
      </header>
      <div className="st-winners__list">
        {WINNERS.map((person) => (
          <article className="st-winner" key={person.slug}>
            <div className="st-winner__img">
              <Photo src={person.image} alt={person.gallery[0]?.caption ?? person.name} sizes="(max-width: 900px) 100vw, 30vw" />
            </div>
            <div className="st-winner__copy">
              <span className="gh-pill st-pill">{person.role}</span>
              <h3>{person.name}</h3>
              <p className="st-winner__summary">{person.summary}</p>
              <p>{person.story}</p>
              <ul aria-label="Highlights">
                {person.highlights.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <a className="gh-link" href={person.sourceUrl} target="_blank" rel="noreferrer">
                {person.sourceLabel} <ArrowUpRight weight="bold" aria-hidden="true" />
              </a>
            </div>
          </article>
        ))}
      </div>
      <Link className="gh-link" to="/programmes/aurora">See Aurora's results <ArrowRight weight="bold" aria-hidden="true" /></Link>
    </section>
  );
}

export default function StoriesHubPage() {
  return (
    <PageFrame className="rd-page st-page">
      <Seo title="Stories and Field Notes" path="/stories" description="Stories, programme notes and learning from the Project GRID community." />
      <main className="st-hub">
        <Masthead />
        <CoverStory />
        <MoreToRead />
        <AarohInPictures />
        <FieldNotesRail />
        <AuroraBuilders />
        <p className="st-correction">
          Something wrong in a story, a caption or a name? Write to <a href="mailto:founder@projectgrid.org">founder@projectgrid.org</a> with the page address.
        </p>
      </main>
    </PageFrame>
  );
}
